let localizationLastInputWasPointer = false;
document.addEventListener('pointerdown', () => { localizationLastInputWasPointer = true; }, true);
document.addEventListener('keydown', () => { localizationLastInputWasPointer = false; }, true);

class LocalizationPanel extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.form = this.closest('form');
    this.search = this.querySelector('[data-localization-search]');
    this.empty = this.querySelector('[data-localization-empty]');
    this.countryList = this.querySelector('[data-localization-list="country"]');
    this.languageToggle = this.querySelector('[data-localization-language-toggle]');
    this.languageList = this.querySelector('[data-localization-list="language"]');
    this.languageWrap = this.querySelector('[data-localization-language-list]');

    this.search?.addEventListener('input', () => this.filter(this.search.value));
    this.search?.addEventListener('pointerdown', () => this.search.classList.add('focus-quiet'));
    this.search?.addEventListener('blur', () => this.search.classList.remove('focus-quiet'));
    this.search?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.focusOption(this.countryList, 0); }
    });

    this.languageToggle?.addEventListener('click', () => this.toggleLanguages());

    this.addEventListener('click', (e) => {
      const option = e.target.closest('[role="option"]');
      if (option) this.select(option);
    });

    this.addEventListener('keydown', (e) => this.onKeydown(e));
  }

  reset() {
    if (this.search) {
      this.search.value = '';
      this.filter('');
    }
    if (this.languageWrap) this.toggleLanguages(false);
  }

  filter(query) {
    if (!this.countryList) return;
    const q = query.trim().toLowerCase();
    let matches = 0;
    this.countryList.querySelectorAll('[data-localization-option]').forEach((item) => {
      const option = item.querySelector('[role="option"]');
      const hit = !q || (option.dataset.search || '').includes(q);
      item.hidden = !hit;
      if (hit) matches++;
    });
    if (this.empty) this.empty.hidden = matches > 0;
  }

  toggleLanguages(force) {
    if (!this.languageList || !this.languageToggle || !this.languageWrap) return;
    const open = typeof force === 'boolean' ? force : !this.languageWrap.classList.contains('is-open');
    this.languageWrap.classList.toggle('is-open', open);
    this.languageToggle.setAttribute('aria-expanded', open);
    this.languageToggle.classList.toggle('is-open', open);
    if (open) {
      const selected = this.languageList.querySelector('[aria-selected="true"]');
      (selected || this.languageList.querySelector('[role="option"]'))?.focus({ preventScroll: true });
    }
  }

  visibleOptions(list) {
    return Array.from(list.querySelectorAll('[data-localization-option]:not([hidden]) [role="option"]'));
  }

  focusOption(list, index) {
    if (!list) return;
    const options = this.visibleOptions(list);
    if (!options.length) return;
    const i = ((index % options.length) + options.length) % options.length;
    options[i].focus();
  }

  onKeydown(e) {
    const option = e.target.closest('[role="option"]');
    if (!option) return;
    const list = option.closest('[data-localization-list]');
    const options = this.visibleOptions(list);
    const index = options.indexOf(option);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); this.focusOption(list, index + 1); break;
      case 'ArrowUp':
        e.preventDefault();
        if (index === 0 && this.search && list === this.countryList) this.search.focus();
        else this.focusOption(list, index - 1);
        break;
      case 'Home': e.preventDefault(); this.focusOption(list, 0); break;
      case 'End': e.preventDefault(); this.focusOption(list, options.length - 1); break;
      default:
        if (this.search && list === this.countryList && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          this.search.focus();
        }
    }
  }

  select(option) {
    if (option.getAttribute('aria-selected') === 'true') return;
    const type = option.closest('[data-localization-list]')?.dataset.localizationList;
    const input = this.form?.querySelector(`input[name="${type}_code"]`);
    if (!input || !this.form) return;
    input.value = option.dataset.value;
    this.querySelectorAll('[role="option"]').forEach((el) => el.setAttribute('aria-selected', el === option));
    this.setAttribute('aria-busy', 'true');
    this.form.submit();
  }
}

class LocalizationSelector extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.panel = this.querySelector('localization-panel');
    this.popoverEl = this.querySelector('.localization-selector__popover');
    if (!this.details || !this.summary) return;

    this.summary.addEventListener('click', (e) => {
      if (!this.details.open) return;
      e.preventDefault();
      this.close();
    });

    this.details.addEventListener('toggle', () => {
      const open = this.details.open;
      this.summary.setAttribute('aria-expanded', open);
      if (!open) return;
      document.querySelectorAll('localization-selector details[open]').forEach((d) => {
        if (d !== this.details) d.open = false;
      });
      this.panel?.reset();
      requestAnimationFrame(() => {
        const target = this.panel?.search || this.panel?.querySelector('[aria-selected="true"]') || this.panel?.querySelector('[role="option"]');
        if (!target) return;
        if (target === this.panel.search && localizationLastInputWasPointer) target.classList.add('focus-quiet');
        target.focus({ preventScroll: true });
      });
    });

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.details.open) return;
      e.stopPropagation();
      this.close();
      this.summary.focus();
    });

    this.addEventListener('focusout', (e) => {
      if (!this.details.open) return;
      if (e.relatedTarget && this.contains(e.relatedTarget)) return;
      if (e.relatedTarget === null) return;
      this.close();
    });

    document.addEventListener('click', (e) => {
      if (this.details.open && !this.contains(e.target)) this.close();
    });
  }

  close() {
    if (!this.details.open || this._closing) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.popoverEl || reduce) {
      this.details.open = false;
      return;
    }
    this._closing = true;
    this.popoverEl.classList.add('is-closing');
    const finish = () => {
      if (!this._closing) return;
      this._closing = false;
      this.popoverEl.classList.remove('is-closing');
      this.details.open = false;
    };
    this.popoverEl.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 260);
  }
}

if (!customElements.get('localization-panel')) customElements.define('localization-panel', LocalizationPanel);
if (!customElements.get('localization-selector')) customElements.define('localization-selector', LocalizationSelector);

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-localization-sheet-trigger]');
  if (!trigger) return;
  e.preventDefault();
  const sheet = document.getElementById('localization-sheet');
  if (!sheet || typeof sheet.show !== 'function') return;
  const menu = document.getElementById('menu-drawer');
  if (menu && menu.hasAttribute('open') && typeof menu.hide === 'function') menu.hide();
  sheet.show(trigger);
  sheet.querySelector('localization-panel')?.reset();
  // the menu drawer refocuses its opener on a timeout; keep focus in the sheet
  setTimeout(() => sheet.querySelector('[role="dialog"]')?.focus(), 60);
});
